import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Play, ArrowLeft, Loader2, Calculator, CheckCircle,
  FileText, RefreshCw, Calendar, DollarSign
} from 'lucide-react';

export default function TributarioApuracao() {
  const [loading, setLoading] = useState(false);
  const [apurando, setApurando] = useState(false);
  const [apuracoes, setApuracoes] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [filtros, setFiltros] = useState({
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear(),
    tipo: 'mensal'
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    loadApuracoes();
  }, [filtros.ano]);

  const loadApuracoes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tributario/apuracoes', {
        params: { ano: filtros.ano }
      });
      setApuracoes(response.data.apuracoes || []);
    } catch (error) {
      console.error('Erro ao carregar apurações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApurar = async (forcarRecalculo = false) => {
    setApurando(true);
    setResultado(null);
    try {
      const response = await api.post('/tributario/apurar', {
        competencia_mes: filtros.mes,
        competencia_ano: filtros.ano,
        tipo: filtros.tipo,
        forcar_recalculo: forcarRecalculo
      });
      
      if (response.data.ja_calculado && !forcarRecalculo) {
        setResultado(response.data.apuracao);
        toast.info('Apuração já existente. Clique em "Recalcular" para atualizar.');
      } else {
        setResultado(response.data);
        toast.success('Apuração realizada com sucesso!');
        loadApuracoes();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao apurar tributos');
    } finally {
      setApurando(false);
    }
  };

  const meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  return (
    <div className="p-6 space-y-6" data-testid="tributario-apuracao">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tributario')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="w-6 h-6" />
            Apuração de Tributos
          </h1>
          <p className="text-muted-foreground">Calcule tributos com memória de cálculo detalhada</p>
        </div>
      </div>

      {/* Filtros e Ação */}
      <Card>
        <CardHeader>
          <CardTitle>Nova Apuração</CardTitle>
          <CardDescription>Selecione o período e tipo de apuração</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Mês</Label>
              <Select 
                value={String(filtros.mes)} 
                onValueChange={(v) => setFiltros(prev => ({ ...prev, mes: parseInt(v) }))}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meses.map(m => (
                    <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Ano</Label>
              <Select 
                value={String(filtros.ano)} 
                onValueChange={(v) => setFiltros(prev => ({ ...prev, ano: parseInt(v) }))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map(ano => (
                    <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select 
                value={filtros.tipo} 
                onValueChange={(v) => setFiltros(prev => ({ ...prev, tipo: v }))}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensal">Mensal</SelectItem>
                  <SelectItem value="trimestral">Trimestral</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={() => handleApurar(false)} disabled={apurando}>
              {apurando ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Calculando...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Apurar Tributos
                </>
              )}
            </Button>

            {resultado && (
              <Button variant="outline" onClick={() => handleApurar(true)} disabled={apurando}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Recalcular
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Resultado da Apuração */}
      {resultado && (
        <div className="space-y-4">
          {/* Cards de Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Receita Base</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">
                  R$ {(resultado.receitas?.base_tributavel || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Total Tributos</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-purple-600">
                  R$ {(resultado.totais?.total_tributos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Carga s/ Faturamento</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">
                  {(resultado.totais?.carga_tributaria_faturamento || 0).toFixed(2)}%
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Carga s/ Recebimento</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">
                  {(resultado.totais?.carga_tributaria_recebimento || 0).toFixed(2)}%
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Detalhamento de Tributos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Detalhamento por Tributo
              </CardTitle>
              <CardDescription>
                Competência: {resultado.competencia_mes?.toString().padStart(2, '0')}/{resultado.competencia_ano}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* PIS */}
                <Card className="bg-blue-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">PIS</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-xl font-bold text-blue-700">
                      R$ {(resultado.tributos?.pis?.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <div className="text-xs text-blue-600 space-y-1">
                      <p>Base: R$ {(resultado.tributos?.pis?.base_calculo || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      <p>Alíquota: {resultado.tributos?.pis?.aliquota_percentual}</p>
                      <p className="italic">{resultado.tributos?.pis?.formula}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* COFINS */}
                <Card className="bg-green-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">COFINS</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-xl font-bold text-green-700">
                      R$ {(resultado.tributos?.cofins?.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <div className="text-xs text-green-600 space-y-1">
                      <p>Base: R$ {(resultado.tributos?.cofins?.base_calculo || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      <p>Alíquota: {resultado.tributos?.cofins?.aliquota_percentual}</p>
                      <p className="italic">{resultado.tributos?.cofins?.formula}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* ISS */}
                <Card className="bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">ISS</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-xl font-bold text-amber-700">
                      R$ {(resultado.tributos?.iss?.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <div className="text-xs text-amber-600 space-y-1">
                      <p>Base: R$ {(resultado.tributos?.iss?.base_calculo || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      <p>Alíquota: {resultado.tributos?.iss?.aliquota_percentual}</p>
                      <p className="italic">{resultado.tributos?.iss?.formula}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* IRPJ */}
                <Card className="bg-purple-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">IRPJ</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-xl font-bold text-purple-700">
                      R$ {(resultado.tributos?.irpj?.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <div className="text-xs text-purple-600 space-y-1">
                      <p>Receita: R$ {(resultado.tributos?.irpj?.receita_bruta || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      <p>Presunção: {resultado.tributos?.irpj?.presuncao_percentual}</p>
                      <p>Base Presumida: R$ {(resultado.tributos?.irpj?.base_presumida || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      <p>IRPJ Base (15%): R$ {(resultado.tributos?.irpj?.irpj_base || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      <p>Adicional (10%): R$ {(resultado.tributos?.irpj?.adicional_valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* CSLL */}
                <Card className="bg-red-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">CSLL</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-xl font-bold text-red-700">
                      R$ {(resultado.tributos?.csll?.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <div className="text-xs text-red-600 space-y-1">
                      <p>Receita: R$ {(resultado.tributos?.csll?.receita_bruta || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      <p>Presunção: {resultado.tributos?.csll?.presuncao_percentual}</p>
                      <p>Base Presumida: R$ {(resultado.tributos?.csll?.base_presumida || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      <p>Alíquota: {resultado.tributos?.csll?.aliquota_percentual}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Totais */}
                <Card className="bg-gray-100">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">TOTAIS</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="text-sm space-y-1">
                      <p>Federais: <strong>R$ {(resultado.totais?.tributos_federais || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></p>
                      <p>Municipais: <strong>R$ {(resultado.totais?.tributos_municipais || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></p>
                      <hr className="my-2" />
                      <p className="text-lg font-bold">
                        Total: R$ {(resultado.totais?.total_tributos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Link para Memória de Cálculo */}
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => navigate(`/tributario/memoria?id=${resultado.id}`)}>
              <FileText className="w-4 h-4 mr-2" />
              Ver Memória de Cálculo Completa
            </Button>
          </div>
        </div>
      )}

      {/* Lista de Apurações Anteriores */}
      <Card>
        <CardHeader>
          <CardTitle>Apurações Anteriores - {filtros.ano}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : apuracoes.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhuma apuração realizada neste ano.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Competência</th>
                    <th className="text-left py-2">Tipo</th>
                    <th className="text-right py-2">Receita Base</th>
                    <th className="text-right py-2">Total Tributos</th>
                    <th className="text-right py-2">Carga %</th>
                    <th className="text-left py-2">Data Cálculo</th>
                    <th className="text-center py-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {apuracoes.map(ap => (
                    <tr key={ap.id} className="border-b hover:bg-muted/50">
                      <td className="py-2 font-medium">
                        {ap.competencia_mes?.toString().padStart(2, '0')}/{ap.competencia_ano}
                      </td>
                      <td className="py-2">
                        <Badge variant="outline">{ap.tipo}</Badge>
                      </td>
                      <td className="text-right py-2">
                        R$ {(ap.receitas?.base_tributavel || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right py-2 font-medium text-purple-600">
                        R$ {(ap.totais?.total_tributos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right py-2">
                        {(ap.totais?.carga_tributaria_faturamento || 0).toFixed(2)}%
                      </td>
                      <td className="py-2 text-muted-foreground">
                        {ap.created_at ? new Date(ap.created_at).toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td className="py-2 text-center">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/tributario/memoria?id=${ap.id}`)}
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
